// GraphQL API endpoint (temporary dummy URL)
const GRAPHQL_API_URL = import.meta.env.VITE_GRAPHQL_URL;
const CAPTCHA_KEY = import.meta.env.VITE_CAPTCHA_KEY;

// Check for invitation_code in URL query parameters and store in localStorage
function storeInvitationCode() {
  const urlParams = new URLSearchParams(window.location.search);
  const invitationCode = urlParams.get('invitation_code');
  if (invitationCode) {
    localStorage.setItem('invitationCode', invitationCode);
    // Optional: Clean the URL by removing the query parameter
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
}

// Call this immediately to store invitation code
storeInvitationCode();

// Load reCAPTCHA script dynamically
if (CAPTCHA_KEY && typeof window !== 'undefined') {
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${CAPTCHA_KEY}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

// Spam prevention and form handling
class LeadCollector {
  constructor() {
    this.form = document.getElementById("leadForm");
    this.submitBtn = document.getElementById("submitBtn");
    this.formGroup = document.getElementById("formGroup");
    this.formMessage = document.getElementById("formMessage");
    this.isSubmitting = false;
    this.apiUrl = GRAPHQL_API_URL;
    this.messageTimeout = null;

    this.init();

    // Initialize fade transitions
    // submitBtn already has transition classes from Tailwind
    if (this.formGroup) this.formGroup.classList.add("fade-element");
  }

  init() {
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    }

    // Add input validation listeners
    const inputs = this.form.querySelectorAll("input[required]");
    inputs.forEach((input) => {
      input.addEventListener("blur", () => this.validateField(input));
      input.addEventListener("input", () => this.clearFieldError(input));
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;

    // Basic spam prevention checks
    if (!this.passesSpamChecks()) {
      this.showTranslatedMessage("ui.tryAgain", "error");
      return;
    }

    const formData = new FormData(this.form);
    const leadData = {
      email: formData.get("email").trim(),
    };

    // Validate data
    if (!this.validateData(leadData)) {
      return;
    }

    this.setSubmitState(true);

    try {
      const token = await this.getRecaptchaToken();
      await this.sendJoinWaitlistMutation(leadData, token);

      this.showTranslatedMessage("form.success", "success");
      this.form.reset();

      // Update rate limiting
      this.updateRateLimit();
    } catch (error) {
      console.error("Error saving lead:", error);
      console.error("API URL:", this.apiUrl);
      console.error("Environment variables - GRAPHQL_URL:", GRAPHQL_API_URL, "CAPTCHA_KEY:", CAPTCHA_KEY ? "set" : "not set");
      this.showTranslatedMessage("form.error", "error");
    } finally {
      this.setSubmitState(false);
    }
  }

  async sendJoinWaitlistMutation({ email }, token) {
    const invitationCode = localStorage.getItem('invitationCode');

    const mutation = `
      mutation JoinWaitlist($email: String!, $recaptchaToken: String!, $invitationCode: String) {
        joinWaitlist(email: $email, recaptchaToken: $recaptchaToken, invitationCode: $invitationCode)
      }
    `;

    const variables = { email, recaptchaToken: token, invitationCode };

    const response = await fetch(this.apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: mutation,
        variables: variables,
      }),
    });

    if (!response.ok) {
      throw new Error("Network error while joining waitlist");
    }

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message || "GraphQL error");
    }

    // Delete invitation code from localStorage after successful call
    localStorage.removeItem('invitationCode');

    return result.data?.joinWaitlist;
  }

  getRecaptchaToken() {
    return new Promise((resolve) => {
      if (!window.grecaptcha) {
        console.warn("reCAPTCHA not loaded");
        resolve(null);
        return;
      }
      grecaptcha.ready(() => {
        grecaptcha
          .execute(CAPTCHA_KEY, { action: "submit" })
          .then((token) => {
            resolve(token);
          })
          .catch((err) => {
            console.error("reCAPTCHA error:", err);
            resolve(null);
          });
      });
    });
  }

  passesSpamChecks() {
    // Check honeypot field
    const honeypot = this.form.querySelector('input[name="website"]');
    if (honeypot && honeypot.value) {
      console.warn("Honeypot field filled - likely spam");
      return false;
    }

    // Check rate limiting (max 3 submissions per hour)
    const submissions = this.getRecentSubmissions();
    if (submissions.length >= 3) {
      console.warn("Rate limit exceeded");
      return false;
    }

    return true;
  }

  validateData(data) {
    const errors = [];

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email || !emailRegex.test(data.email)) {
      const message = window.i18n
        ? window.i18n.t("form.validation.emailRequired")
        : "Please enter a valid email address";
      errors.push(message);
    }

    if (errors.length > 0) {
      this.showMessage(errors.join("<br>"), "error");
      return false;
    }

    return true;
  }

  validateField(field) {
    const value = field.value.trim();
    field.classList.remove("error");

    if (field.hasAttribute("required") && !value) {
      const message = window.i18n
        ? window.i18n.t("form.validation.fieldRequired")
        : "This field is required";
      this.setFieldError(field, message);
      return false;
    }

    if (field.type === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        const message = window.i18n
          ? window.i18n.t("form.validation.emailInvalid")
          : "Please enter a valid email address";
        this.setFieldError(field, message);
        return false;
      }
    }

    return true;
  }

  setFieldError(field, message) {
    field.classList.add("error");
    // You could add individual field error messages here
  }

  clearFieldError(field) {
    field.classList.remove("error");
  }

  getRecentSubmissions() {
    const submissions = JSON.parse(localStorage.getItem("submissions") || "[]");
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return submissions.filter((time) => time > oneHourAgo);
  }

  updateRateLimit() {
    const submissions = this.getRecentSubmissions();
    submissions.push(Date.now());
    localStorage.setItem("submissions", JSON.stringify(submissions));
  }

  setSubmitState(isSubmitting) {
    this.isSubmitting = isSubmitting;
    this.submitBtn.disabled = isSubmitting;

    const btnText = this.submitBtn.querySelector(".btn-text");
    const btnLoading = this.submitBtn.querySelector(".btn-loading");

    if (isSubmitting) {
      btnText.style.display = "none";
      btnLoading.style.display = "inline";
    } else {
      btnText.style.display = "inline";
      btnLoading.style.display = "none";
    }
  }

  showMessage(message, type) {
    // Remove any existing timeout
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    this.formMessage.innerHTML = message;

    // Set background color based on message type
    if (type === "success") {
      this.formMessage.className = "fixed bottom-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full shadow-lg text-white font-medium text-center transition-all duration-300 opacity-0 translate-y-4 pointer-events-none z-50 max-w-sm bg-green-500";
      this.formMessage.classList.remove("opacity-0", "translate-y-4", "pointer-events-none");
      this.formMessage.classList.add("opacity-100", "translate-y-0", "pointer-events-auto");
    } else if (type === "error") {
      this.formMessage.className = "fixed bottom-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full shadow-lg text-white font-medium text-center transition-all duration-300 opacity-0 translate-y-4 pointer-events-none z-50 max-w-sm bg-red-500";
      this.formMessage.classList.remove("opacity-0", "translate-y-4", "pointer-events-none");
      this.formMessage.classList.add("opacity-100", "translate-y-0", "pointer-events-auto");
    }

    // Auto-hide after 5 seconds
    this.messageTimeout = setTimeout(() => {
      this.formMessage.classList.remove("opacity-100", "translate-y-0", "pointer-events-auto");
      this.formMessage.classList.add("opacity-0", "translate-y-4", "pointer-events-none");
    }, 5000);
  }

  showTranslatedMessage(translationKey, type) {
    const message = window.i18n
      ? window.i18n.t(translationKey)
      : translationKey;
    this.showMessage(message, type);
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  new LeadCollector();

  // Initialize language switcher
  initLanguageSwitcher();
});

// Language switcher functionality
function initLanguageSwitcher() {
  const languageSelect = document.getElementById("languageSelect");

  if (languageSelect) {
    // Set initial value based on current language
    languageSelect.value = window.i18n.getCurrentLanguage();

    // Handle language change
    languageSelect.addEventListener("change", (e) => {
      window.i18n.switchLanguage(e.target.value);
    });

    // Listen for language changes from other sources
    document.addEventListener("languageChanged", (e) => {
      languageSelect.value = e.detail.language;
    });
  }
}

// Optional: Add some smooth scrolling for better UX
document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener("click", function (e) {
    e.preventDefault();
    const target = document.querySelector(this.getAttribute("href"));
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  });
});

// Add CSS for field validation errors
const style = document.createElement("style");
style.textContent = `
    .form-group input.error {
        border-color: #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
`;
document.head.appendChild(style);
